import React, { useContext, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Alert, List, Select } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import { singularPluralFormat } from '../../../helpers/format';
import { triggerNotification } from '../../../helpers/notificationHelper';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';

const PaymentPlanChangeStatusModal = ({
  isOpen,
  onClose,
  pkRegAssociation,
  pkRegAssociationPaymentPlans
}) => {
  const { updateAdminRegAssociationPaymentPlans } = useContext(
    RegistrationAdminContext
  );
  const [formState, setFormState] = useState({
    active: true,
    loading: false
  });
  const [failedPaymentPlans, setFailedPaymentPlans] = useState([]);

  const hasFailures = failedPaymentPlans.length > 0;

  useEffect(() => {
    if (isOpen) {
      setFormState({
        active: true,
        loading: false
      });
      setFailedPaymentPlans([]);
    }
  }, [isOpen]);

  const onSubmit = () => {
    setFormState({ ...formState, loading: true });
    setFailedPaymentPlans([]);
    updateAdminRegAssociationPaymentPlans(
      pkRegAssociation,
      {
        regAssociationPaymentPlans: pkRegAssociationPaymentPlans.map(
          (plan) => ({
            pkRegAssociationPaymentPlan: plan.pkRegAssociationPaymentPlan,
            active: formState.active
          })
        )
      },
      (data) => {
        const requestedPks = pkRegAssociationPaymentPlans.map(
          (plan) => plan.pkRegAssociationPaymentPlan
        );
        const failedPlans = (data ?? []).filter(
          (plan) =>
            requestedPks.includes(plan.pkRegAssociationPaymentPlan) &&
            plan.errors?.length > 0
        );

        if (failedPlans.length > 0) {
          setFailedPaymentPlans(failedPlans);
          setFormState({ ...formState, loading: false });
          triggerNotification(
            `${singularPluralFormat(
              failedPlans.length,
              'payment plan',
              'payment plans'
            )} could not be activated.`,
            'warning'
          );
        }
        else {
          triggerNotification('Payment plans updated!', 'success');
          onClose();
        }
      },
      (error) => {
        triggerNotification(error);
        setFormState({ ...formState, loading: false });
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={{
        cancelTitle: hasFailures ? 'Close' : undefined,
        isLoading: formState.loading,
        isSubmitHidden: hasFailures,
        onCancel: onClose,
        onSubmit,
        submitTitle: 'Confirm'
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      size={600}
      title={
        hasFailures
          ? 'Payment plans could not be activated'
          : 'Change payment plan status?'
      }
    >
      {hasFailures && (
        <AppStack style={{ gap: 8 }}>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            The following plans were left disabled and are not available during
            registration.
          </AppText>

          <Alert color="red" title="" variant="outline">
            <AppStack style={{ gap: 10 }}>
              {failedPaymentPlans.map((plan) => (
                <AppStack
                  key={plan.pkRegAssociationPaymentPlan}
                  style={{ gap: 0 }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {plan.name}
                  </AppText>
                  <List withPadding>
                    {plan.errors.map((error, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <List.Item key={index}>
                        <AppText style={{ fontSize: 14 }}>{error}</AppText>
                      </List.Item>
                    ))}
                  </List>
                </AppStack>
              ))}
            </AppStack>
          </Alert>
        </AppStack>
      )}

      {!hasFailures && pkRegAssociationPaymentPlans && pkRegAssociation && (
        <AppStack style={{ gap: 8 }}>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Setting status for
            {singularPluralFormat(
              pkRegAssociationPaymentPlans.length,
              'plan',
              'plans'
            )}
            .
          </AppText>

          <AppStack style={{ gap: 20 }}>
            <Select
              data={[
                {
                  label: 'Active',
                  value: 'active'
                },
                {
                  label: 'Disabled',
                  value: 'disabled'
                }
              ]}
              disabled={formState.loading}
              label="New status"
              onChange={(v) => {
                if (v) {
                  setFormState({
                    ...formState,
                    active: v === 'active'
                  });
                }
              }}
              required
              searchable
              value={formState.active ? 'active' : 'disabled'}
            />

            <Alert color="blue" title="" variant="outline">
              <AppStack style={{ gap: 10 }}>
                <AppText style={{ fontSize: 14 }}>
                  Are you sure you want to <b>change the status</b> of{' '}
                  <b>
                    {singularPluralFormat(
                      pkRegAssociationPaymentPlans.length,
                      'plan',
                      'plans'
                    )}
                  </b>{' '}
                  to <b>{formState.active ? 'Active' : 'Disabled'}</b>?
                </AppText>

                {formState.active ? (
                  <>
                    <AppText style={{ fontSize: 14 }}>
                      Activating this plan will make it <b>available</b> during
                      registration, allowing users to select it when they
                      register.
                    </AppText>
                    <AppText
                      style={{
                        fontSize: 14,
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      If any plans are invalid, they will automatically be
                      reverted to Disabled.
                    </AppText>
                  </>
                ) : (
                  <AppText style={{ fontSize: 14 }}>
                    Disabling this payment plan will make it unavailable during
                    registration.
                  </AppText>
                )}
              </AppStack>
            </Alert>
          </AppStack>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

PaymentPlanChangeStatusModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  pkRegAssociation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pkRegAssociationPaymentPlans: PropTypes.array
};

export default PaymentPlanChangeStatusModal;
